import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Construction } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { AdminOrders } from "./AdminOrders";
import { AdminProducts } from "./AdminProducts";
import { AdminSettings } from "./AdminSettings";
import { AdminLabels } from "./AdminLabels";
import { AdminLabResults } from "./AdminLabResults";
import { AdminUsers } from "./AdminUsers";
import { AdminStats } from "./AdminStats";
import { AdminAnalytics } from "./AdminAnalytics";
import { AdminCoupons } from "./AdminCoupons";
import { AdminBundles } from "./AdminBundles";
import { AdminActivity } from "./AdminActivity";
import { AdminInbox } from "./AdminInbox";
import { AdminBulletin } from "./AdminBulletin";
import { AdminTicker } from "./AdminTicker";
import { AdminGuides } from "./AdminGuides";
import { Seo } from "../../components/Seo";

function SiteModeToggle() {
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-mode").then(({ data }) => setMode(data.under_construction)).catch(() => setMode(false));
  }, []);

  const toggle = async (checked) => {
    setSaving(true);
    try {
      await api.put("/admin/site-mode", { under_construction: checked });
      setMode(checked);
      toast.success(checked
        ? "Site is now hidden behind the Under Construction page"
        : "Site is now live for everyone");
    } catch {
      toast.error("Failed to update site mode");
    } finally {
      setSaving(false);
    }
  };

  if (mode === null) return null;

  return (
    <div
      className={`flex items-center justify-between gap-4 border rounded-lg px-6 py-4 mb-8 ${mode ? "border-[#B45309] bg-[#FEF3E2]" : "border-[#DDD7CF] bg-white"}`}
      data-testid="site-mode-card"
    >
      <div className="flex items-center gap-4 min-w-0">
        <Construction className={`w-6 h-6 shrink-0 ${mode ? "text-[#B45309]" : "text-[#8C7B6B]"}`} />
        <div className="min-w-0">
          <p className="font-display text-sm uppercase tracking-wide text-[#1A1A1A]">Under Construction Mode</p>
          <p className="text-xs text-[#6E675F] mt-0.5" data-testid="site-mode-status">
            {mode
              ? "ON — visitors see only the Under Construction page. Admins can log in and use the full site."
              : "OFF — the store is visible to everyone."}
          </p>
        </div>
      </div>
      <Switch
        checked={mode}
        onCheckedChange={toggle}
        disabled={saving}
        data-testid="under-construction-toggle"
        aria-label="Toggle under construction mode"
      />
    </div>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "orders";
  const [inboxCount, setInboxCount] = useState(0);
  const [bulletinDrafts, setBulletinDrafts] = useState(0);
  const [guideDrafts, setGuideDrafts] = useState(0);

  const refreshInboxCount = useCallback(() => {
    api.get("/admin/support/chats/open-count")
      .then(({ data }) => setInboxCount(data.count))
      .catch(() => {});
    api.get("/admin/bulletin/draft-count")
      .then(({ data }) => setBulletinDrafts(data.count))
      .catch(() => {});
    api.get("/admin/guides/draft-count")
      .then(({ data }) => setGuideDrafts(data.count))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    refreshInboxCount();
    const t = setInterval(refreshInboxCount, 30000);
    return () => clearInterval(t);
  }, [user, refreshInboxCount]);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/");
  }, [user, loading, navigate]);

  if (loading || !user?.is_admin) {
    return (
      <div className="relative z-10 flex justify-center py-40">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="admin-panel">
      <Seo title="Admin" path="/admin" noindex />
      <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-2">Admin Panel</p>
      <h1 className="font-display text-lg uppercase tracking-wide sm:text-3xl uppercase tracking-wide text-[#1A1A1A] mb-10">Store Management</h1>

      <SiteModeToggle />

      <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList className="mb-8 h-auto flex flex-wrap justify-start gap-1 max-w-full">
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="admin-tab-inbox">
            Inbox
            {inboxCount > 0 && (
              <span
                data-testid="admin-inbox-badge"
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-none bg-[#8C7B6B] text-white text-[10px] font-bold"
              >
                {inboxCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="admin-tab-stats">Stats</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="ticker" data-testid="admin-tab-ticker">Ticker-Tape</TabsTrigger>
          <TabsTrigger value="bundles" data-testid="admin-tab-bundles">Bundles</TabsTrigger>
          <TabsTrigger value="labresults" data-testid="admin-tab-labresults">Lab Results</TabsTrigger>
          <TabsTrigger value="labels" data-testid="admin-tab-labels">Labels</TabsTrigger>
          <TabsTrigger value="bulletin" data-testid="admin-tab-bulletin">
            Bulletin
            {bulletinDrafts > 0 && (
              <span
                data-testid="admin-bulletin-badge"
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-none bg-[#B8860B] text-white text-[10px] font-bold"
              >
                {bulletinDrafts}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="guides" data-testid="admin-tab-guides">
            Guides
            {guideDrafts > 0 && (
              <span
                data-testid="admin-guides-badge"
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-none bg-[#B8860B] text-white text-[10px] font-bold"
              >
                {guideDrafts}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          {user.is_super_admin && (
            <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="orders"><AdminOrders /></TabsContent>
        <TabsContent value="inbox"><AdminInbox onChanged={refreshInboxCount} /></TabsContent>
        <TabsContent value="stats"><AdminStats /></TabsContent>
        <TabsContent value="analytics"><AdminAnalytics /></TabsContent>
        <TabsContent value="products"><AdminProducts /></TabsContent>
        <TabsContent value="users"><AdminUsers /></TabsContent>
        <TabsContent value="coupons"><AdminCoupons /></TabsContent>
        <TabsContent value="ticker"><AdminTicker /></TabsContent>
        <TabsContent value="bundles"><AdminBundles /></TabsContent>
        <TabsContent value="labresults"><AdminLabResults /></TabsContent>
        <TabsContent value="labels"><AdminLabels /></TabsContent>
        <TabsContent value="bulletin"><AdminBulletin /></TabsContent>
        <TabsContent value="guides"><AdminGuides onChanged={refreshInboxCount} /></TabsContent>
        <TabsContent value="settings"><AdminSettings /></TabsContent>
        {user.is_super_admin && (
          <TabsContent value="activity"><AdminActivity /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
